import { Box, Typography } from "@mui/material";
import { CloudUploadOutlined } from "@mui/icons-material";
import { useRef, useState, type DragEvent } from "react";

import { UPLOAD_LIMITS } from "./uploadModel";

/** Files of a drop, and how many folders came along (they cannot be uploaded). */
function dropped(event: DragEvent): { files: File[]; folders: number } {
  const folders = new Set(
    Array.from(event.dataTransfer.items ?? [])
      .map((item) => (item.kind === "file" ? item.webkitGetAsEntry?.() : null))
      .filter((entry) => entry?.isDirectory)
      .map((entry) => entry?.name),
  );
  // A folder also arrives as a typeless File of its own name.
  const files = Array.from(event.dataTransfer.files ?? []).filter(
    (file) => !(file.type === "" && folders.has(file.name)),
  );
  return { files, folders: folders.size };
}

/** The dashed box files are dropped on; anywhere in it, a click or Enter or Space
 * opens the browser's file picker. */
export default function UploadDropZone({
  disabled,
  onFiles,
}: {
  disabled: boolean;
  onFiles: (files: File[], folders: number) => void;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  // dragenter and dragleave fire for every child the pointer crosses.
  const depth = useRef(0);
  const [over, setOver] = useState(false);

  const leave = () => {
    depth.current = 0;
    setOver(false);
  };
  const browse = () => {
    if (!disabled) input.current?.click();
  };

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-label="Add files: drag and drop them here, or browse"
      onClick={browse}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        // Space would scroll the dialog.
        event.preventDefault();
        browse();
      }}
      onDragEnter={(event) => {
        event.preventDefault();
        if (disabled) return;
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={() => {
        depth.current -= 1;
        if (depth.current <= 0) leave();
      }}
      onDrop={(event) => {
        event.preventDefault();
        leave();
        if (disabled) return;
        const { files, folders } = dropped(event);
        onFiles(files, folders);
      }}
      sx={{
        px: 2,
        py: 2.5,
        textAlign: "center",
        border: "1.5px dashed",
        borderColor: over ? "primary.main" : "divider",
        borderRadius: 1,
        bgcolor: over ? "action.hover" : "grey.50",
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "default" : "pointer",
        transition: "border-color 120ms, background-color 120ms",
        "&:hover": disabled
          ? undefined
          : { borderColor: "primary.main", bgcolor: "action.hover" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <input
        ref={input}
        type="file"
        multiple
        hidden
        // The box opens the picker: its own click must not reach the box again.
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          onFiles(Array.from(event.target.files ?? []), 0);
          event.target.value = "";
        }}
      />
      <CloudUploadOutlined sx={{ color: "text.secondary" }} aria-hidden />
      <Typography variant="body2">
        Drag &amp; drop files here, or{" "}
        <Box
          component="span"
          sx={{
            fontWeight: 600,
            color: "primary.main",
            textDecoration: "underline",
          }}
        >
          click to browse
        </Box>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {UPLOAD_LIMITS}
      </Typography>
    </Box>
  );
}
